import React, { createContext, useContext, useReducer, useEffect } from 'react';

const TAX_RATE = 0.18;
const DISCOUNT_AMOUNT = 4160.00;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SHOP_ID = process.env.REACT_APP_SHOP_ID;
// const initialState = {
//   searchState: {
//     hotel: "ABC Hotel",
//     checkin: new Date(),
//     checkout: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
//     guestsText: "1 Room, 2 Guests",
//     guestCount: 2,
//     room_guest: [{ room_no: 1, no_of_adults: 1, no_of_children: 0 }] // ADD THIS
//   },
//   rooms: [{ adults: 1, children: 0 }],
//   cart: [],
//   hasDiscount: false,
//   subtotal: 0,
//   taxes: 0,
//   total: 0,
//   finalTotal: 0,
//   rateTotal: 0,
//   offerDiscount: 0,
//   user: null // Add user to initial state
// };

const createInitialState = () => {
  // FIRST check URL for widget data
  const searchParams = new URLSearchParams(window.location.search);
  const fromWidget = searchParams.get('from_widget');

  if (fromWidget === 'true') {
    console.log('🎯 Creating initial state from widget URL');
    
    try {
      // Extract widget data from URL
      const shopId = searchParams.get('shop_id') || SHOP_ID;
      const hotelId = searchParams.get('hotel_id');
      const hotelNameEncoded = searchParams.get('hotel_name');
      const startDateStr = searchParams.get('start_date');
      const endDateStr = searchParams.get('end_date');
      const roomsCount = searchParams.get('rooms');
      const guestsCount = searchParams.get('guests');
      const roomDataStr = searchParams.get('room_data');
      const clientUrl = searchParams.get('client_url');
      const promoCode = searchParams.get('promo_code');
      
      // Decode values
      const hotelName = hotelNameEncoded ? decodeURIComponent(hotelNameEncoded) : 'Selected Hotel';
      
      // FIX: Parse dates properly to avoid timezone shifts
      const startDate = parseDateString(startDateStr);
      const endDate = parseDateString(endDateStr) || new Date(startDate.getTime() + 2 * 24 * 60 * 60 * 1000);
      
      const rooms = roomsCount ? parseInt(roomsCount) : 1;
      const guests = guestsCount ? parseInt(guestsCount) : 2;
      
      // Parse room data
      let roomData = [];
      if (roomDataStr) {
        roomData = JSON.parse(decodeURIComponent(roomDataStr));
      } else {
        roomData = [{ room_no: 1, adults: 2, children: 0 }];
      }
      
      // Create room_guest array
      const room_guest = roomData.map((room, index) => ({
        room_no: room.room_no || index + 1,
        no_of_adults: room.adults || 2,
        no_of_children: room.children || 0
      }));
      
      // Create rooms array for UI
      const roomsArray = roomData.map((room, index) => ({
        adults: room.adults || 2,
        children: room.children || 0,
        childAges: Array(room.children || 0).fill(5)
      }));
      
      console.log('📦 Initial state from widget:', {
        hotelName,
        hotelId,
        startDate: startDate.toString(),
        endDate: endDate.toString(),
        roomsArray,
        clientUrl,
        promoCode
      });
      
      // Store client_url in sessionStorage
      if (clientUrl) {
        sessionStorage.setItem('client_url', clientUrl);
        console.log('✅ Stored client_url in sessionStorage:', clientUrl);
      }
      
      return {
        searchState: {
          hotel: hotelName,
          hotelId: hotelId,
          checkin: startDate,
          checkout: endDate,
          guestsText: `${rooms} Room${rooms !== 1 ? 's' : ''}, ${guests} Guest${guests !== 1 ? 's' : ''}`,
          guestCount: guests,
          room_guest: room_guest,
          shop_id: SHOP_ID,
          searchPerformed: false,
          clientUrl: clientUrl || null,
          promo_code: promoCode || null,
          promo_from_widget: !!promoCode
        },
         searchResults: null,
        rooms: roomsArray,
        cart: [],
        hasDiscount: false,
        subtotal: 0,
        taxes: 0,
        total: 0,
        finalTotal: 0,
        rateTotal: 0,
        offerDiscount: 0,
        user: null,
        activePromoCode: null,        // Currently active promo code
        isPromoApplied: false,        // Whether promo is applied
        promoAppliedAt: null,          // Timestamp when promo was applied
        promoSource: null,             // 'searchbar', 'sidebar', 'widget'
        promoDiscountDetails: null,    // Discount details from API
         sidebarPromoCode: null,  // Track promo selected in sidebar
        sidebarPromoApplied: false,
      };
      
    } catch (error) {
      console.error('Error creating initial state from widget:', error);
      return getInitialStateFromSessionOrDefault();
    }
  }
  
  return getInitialStateFromSessionOrDefault();
};

// Helper function to parse date string without timezone issues
function parseDateString(dateStr) {
  if (!dateStr) return new Date();
  
  // If it's in YYYY-MM-DD format
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    // Create date at noon UTC to avoid timezone issues
    // Using UTC ensures the date doesn't shift when converted to local time
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    console.log(`Parsed date ${dateStr} to:`, date.toISOString());
    return date;
  }
  
  // If it's already a Date object or other format
  const date = new Date(dateStr);
  // Set to noon to avoid boundary issues
  date.setHours(12, 0, 0, 0);
  return date;
}

// New function to check session storage first
const getInitialStateFromSessionOrDefault = () => {
  console.log('🔄 getInitialStateFromSessionOrDefault called at:', new Date().toISOString());
  
  try {
    const saved = sessionStorage.getItem('bookingSession');
    if (saved) {
      const parsed = JSON.parse(saved);
      console.log('📦 Creating initial state from session storage:', {
        hotel: parsed.searchState?.hotel,
        hotelId: parsed.searchState?.hotelId,
        timestamp: new Date().toISOString()
      });
      
      // Convert date strings back to Date objects
      if (parsed.searchState) {
        parsed.searchState.checkin = new Date(parsed.searchState.checkin);
        parsed.searchState.checkout = new Date(parsed.searchState.checkout);
        if (!parsed.searchState.room_guest) {
          parsed.searchState.room_guest = [{ room_no: 1, no_of_adults: 1, no_of_children: 0 }];
        }
        // Ensure hotelId is included if not present
        if (!parsed.searchState.hotelId && parsed.searchState.hotel) {
          parsed.searchState.hotelId = undefined;
        }
      }
      return parsed;
    }
  } catch (error) {
    console.error('Error loading from session storage:', error);
  }
  
  // Fall back to default
  console.log('📦 No session storage, using default state');
  return getDefaultInitialState();
};

// Keep the default initial state function as is
const getDefaultInitialState = () => {
  return {
    searchState: {
      hotel: "ABC Hotel",
      checkin: new Date(),
      checkout: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000),
      guestsText: "1 Room, 2 Guests",
      guestCount: 2,
      room_guest: [{ room_no: 1, no_of_adults: 2, no_of_children: 0 }]
    },
    searchResults: null,
    rooms: [{ adults: 2, children: 0 }],
    cart: [],
    hasDiscount: false,
    subtotal: 0,
    taxes: 0,
    total: 0,
    finalTotal: 0,
    rateTotal: 0,
    offerDiscount: 0,
    user: null,
    sidebarPromoCode: null,  // Track promo selected in sidebar
    sidebarPromoApplied: false,
  };
};

function bookingReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_SEARCH_STATE':
      return {
        ...state,
        searchState: { ...state.searchState, ...action.payload }
      };
    
    case 'UPDATE_ROOMS':
      return {
        ...state,
        rooms: action.payload
      };
    
    case 'UPDATE_CART':
      return {
        ...state,
        cart: action.payload,
         taxBreakdown: action.taxBreakdown
      };
    
    // case 'APPLY_DISCOUNT':
    //   return {
    //     ...state,
    //     hasDiscount: true,
    //     finalTotal: Math.max(0, state.total - DISCOUNT_AMOUNT),
    //   };

        // Add to reducer:
    case 'SET_SIDEBAR_PROMO':
      return {
        ...state,
        sidebarPromoCode: action.payload.promoCode,
        sidebarPromoApplied: action.payload.applied,
      };

    case 'CLEAR_SIDEBAR_PROMO':
      return {
        ...state,
        sidebarPromoCode: null,
        sidebarPromoApplied: false,
      };
    
    case 'UPDATE_TOTALS':
      return {
        ...state,
        ...action.payload,
       subtotal: action.payload.subtotal,
        taxes: action.payload.taxes,
        total: action.payload.total,
        finalTotal: action.payload.finalTotal,
        taxBreakdown: action.payload.taxBreakdown || null // Store tax breakdown
      };

      case 'RESTORE_PROMO_STATE':
  return {
    ...state,
    cart: action.payload.cart,
    hasDiscount: action.payload.hasDiscount,
    promoCode: action.payload.promoCode,
    discountAmount: action.payload.discountAmount,
    discountedSubtotal: action.payload.discountedSubtotal,
    discountedTaxes: action.payload.discountedTaxes,
    discountedTotal: action.payload.discountedTotal,
    finalTotal: action.payload.discountedTotal,
    fromWidget: false
  };

  case 'RESTORE_CART_ORIGINAL_PRICES':
  return {
    ...state,
    cart: action.payload.cart,
    subtotal: action.payload.subtotal,
    taxes: action.payload.taxes,
    total: action.payload.total,
    finalTotal: action.payload.finalTotal,
    hasDiscount: false,
    discountAmount: 0,
    promoCode: null,
    discountedSubtotal: null,
    discountedTaxes: null,
    discountedTotal: null,
    fromWidget: false,
  };
    case 'SET_STATE':
      return {
        ...action.payload
      };
      case 'RESTORE_ORIGINAL_CART_STATE':
  return {
    ...state,
    cart: action.payload.cart,
    subtotal: action.payload.subtotal,
    taxes: action.payload.taxes,
    total: action.payload.total,
    finalTotal: action.payload.finalTotal,
    hasDiscount: action.payload.hasDiscount || false,
    discountAmount: action.payload.discountAmount || 0,
    promoCode: action.payload.promoCode || null,
    discountedSubtotal: action.payload.discountedSubtotal || null,
    discountedTaxes: action.payload.discountedTaxes || null,
    discountedTotal: action.payload.discountedTotal || null,
    fromWidget: false,
  };
    
    case 'RESET_SEARCH_PERFORMED':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          searchPerformed: false
        }
      };

      case 'SET_TAX_BREAKDOWN':
      return {
        ...state,
        taxBreakdown: action.payload
      };
    
    // ADD THESE NEW CASES
    case 'SET_SEARCH_PARAMS':
      return {
        ...state,
        searchState: {
          ...state.searchState,
          ...action.payload
        }
      };
    
    case 'PERFORM_SEARCH':
      return {
        ...state,
        searchResults: action.payload.apiResponse,
        searchState: {
          ...state.searchState,
          ...action.payload,
            hotel: state.searchState.hotel || action.payload.hotel,
            hotelId: state.searchState.hotelId || action.payload.hotelId,
            checkin: action.payload.checkin || state.searchState.checkin,
            checkout: action.payload.checkout || state.searchState.checkout,
            searchPerformed: true
        }
      };
    case 'APPLY_DISCOUNT':
      const discountAmount = action.payload.discountAmount;
      return {
        ...state,
        hasDiscount: true,
        discountAmount: discountAmount,
        promoCode: action.payload.promoCode,
        finalTotal: state.total - discountAmount,
        fromWidget: action.payload.fromWidget || false,
         // Recalculate totals based on cart items
        // subtotal: action.payload.reduce((sum, item) => 
        //   sum + (item.taxData?.base_amount_total || item.roomPrice), 0
        // ),
        // taxes: action.payload.reduce((sum, item) => 
        //   sum + (item.taxPerRoom || 0), 0
        // ),
        // total: action.payload.reduce((sum, item) => 
        //   sum + (item.taxData?.grand_total || item.roomPrice + (item.taxPerRoom || 0)), 0
        // )
      };


      // Add this new case for widget promo
    case 'APPLY_WIDGET_PROMO':
      return {
        ...state,
        hasDiscount: true,
        discountAmount: action.payload.discountAmount,
        promoCode: action.payload.promoCode,
        finalTotal: state.total - action.payload.discountAmount,
        fromWidget: true, // Mark as from widget
        promoCodeApplied: true
      };
    

      case 'APPLY_MANUAL_PROMO':
  return {
    ...state,
    cart: action.payload.updatedCart,
    hasDiscount: true,
    discountAmount: action.payload.discountAmount,
    promoCode: action.payload.promoCode,
    discountedSubtotal: action.payload.discountedSubtotal,
    discountedTaxes: action.payload.discountedTaxes,
    discountedTotal: action.payload.discountedTotal,
    finalTotal: action.payload.discountedTotal,
    promoCodeInfo: action.payload.promoCodeInfo,
    fromWidget: false,
     originalSubtotal: action.payload.originalSubtotal || state.subtotal
  };

      
       // Add user actions
    case 'SET_USER':
      return { ...state, user: action.payload };
    
    case 'CLEAR_USER':
      return { ...state, user: null };

    case 'CLEAR_CART':
      return {
        ...state,
        cart: [],
        subtotal: 0,
        total: 0,
        finalTotal: 0,
        taxes: 0,
        hasDiscount: false,
        discountAmount: 0,
        promoCode: '',
        offerDiscount: 0,
        rateTotal: 0
      };

      case 'CLEAR_DISCOUNT':
    return {
      ...state,
      hasDiscount: false,
      discountAmount: 0,
      promoCode: '',
      fromWidget: false,
      discountedSubtotal: null,
      discountedTaxes: null,
      discountedTotal: null,
      finalTotal: null,
      promoCodeInfo: null,
      // Reset cart items' discounted prices
      cart: state.cart.map(item => ({
        ...item,
        discountedPricePerNight: null,
        originalPrice: null
      }))
    };

    // Add to reducer:
case 'APPLY_PROMO':
  return {
    ...state,
    activePromoCode: action.payload.promoCode,
    isPromoApplied: true,
    promoAppliedAt: Date.now(),
    promoSource: action.payload.source,
    promoDiscountDetails: action.payload.discountDetails,
    // Also update searchState to keep sync
    searchState: {
      ...state.searchState,
      promo_code: action.payload.promoCode,
      promo_from_widget: action.payload.source === 'widget'
    }
  };
 case 'CLEAR_PROMO':
  return {
    ...state,
    activePromoCode: null,
    isPromoApplied: false,
    promoAppliedAt: null,
    promoSource: null,
    promoDiscountDetails: null,
    searchState: {
      ...state.searchState,
      promo_code: '',
      promo_from_widget: false
    },
    hasDiscount: false,
    discountAmount: 0,
    promoCode: null,
    discountedSubtotal: null,
    discountedTaxes: null,
    discountedTotal: null,
    fromWidget: false,
  };

    default:
      return state;
  }
}

const BookingContext = createContext();

export function BookingProvider({ children }) {
  const [state, dispatch] = useReducer(bookingReducer, createInitialState());

  // Load from session storage on mount - but ONLY if not from widget
  // useEffect(() => {
  //   // Check if we came from widget (by checking initial state)
  //   if (state.searchState.hotel !== "ABC Hotel") {
  //     console.log('📦 Came from widget, not loading from session storage');
  //     return;
  //   }
    
  //   // Normal flow: Load from session storage
  //   const saved = sessionStorage.getItem('bookingSession');
  //     console.log('📦 Session storage content:', saved);
  //   if (saved) {
  //     try {
  //       const parsed = JSON.parse(saved);
  //       console.log('📦 Parsed session data:', {
  //       hotel: parsed.searchState?.hotel,
  //       hotelId: parsed.searchState?.hotelId,
  //       hasSearchState: !!parsed.searchState
  //     });
  //       // Convert date strings back to Date objects
  //       if (parsed.searchState) {
  //         parsed.searchState.checkin = new Date(parsed.searchState.checkin);
  //         parsed.searchState.checkout = new Date(parsed.searchState.checkout);
  //         if (!parsed.searchState.room_guest) {
  //           parsed.searchState.room_guest = [{ room_no: 1, no_of_adults: 1, no_of_children: 0 }];
  //         }
  //       }
  //       // Set the entire loaded state
  //       dispatch({ type: 'SET_STATE', payload: parsed });
  //     } catch (error) {
  //       console.error('Error loading session data:', error);
  //     }
  //   }
  // }, [state.searchState.hotel]); // Depend on hotel name

  // Save to session storage when state changes
  useEffect(() => {
    sessionStorage.setItem('bookingSession', JSON.stringify(state));
  }, [state]);

  useEffect(() => {
  // When promo is cleared from state, also clear stackable info from localStorage
  if (!state.searchState.promo_code && !state.hasDiscount) {
    console.log('🔄 Promo cleared, clearing stackable info cache');
    localStorage.removeItem('hotel_stackable_info');
  }
}, [state.searchState.promo_code, state.hasDiscount]);

  const formatCurrency = (amount) => {
    const numericAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR', 
      minimumFractionDigits: 0,  // Changed from 2 to 0
     maximumFractionDigits: 0   // Changed from 2 to 0
    }).format(numericAmount);
  };

   const calculateAndUpdateTaxes = async (cart) => {
    const { checkin, checkout, shop_id } = state.searchState;
    const taxResult = await calculateTaxesForCart(cart, checkin, checkout, SHOP_ID);
    
    // Recalculate totals with dynamic taxes
    const nights = calculateNights();
    const rateTotal = cart.reduce((acc, item) => acc + (item.roomPrice * item.quantity * nights), 0);
    
    let offerDiscount = 0;
    cart.forEach(item => {
      if (item.offerFreeNights > 0 && nights >= 3) {
        offerDiscount += (item.roomPrice * item.offerFreeNights * item.quantity);
      }
    });
    
    const subtotal = rateTotal - offerDiscount;
    const taxes = taxResult.taxes;
    const total = subtotal + taxes;
    
    dispatch({
      type: 'UPDATE_TOTALS',
      payload: {
        rateTotal,
        offerDiscount,
        subtotal,
        taxes,
        total,
        finalTotal: total,
        taxBreakdown: taxResult.taxBreakdown
      }
    });
  };
  
  // Add effect to recalculate taxes when cart changes
  useEffect(() => {
    if (state.cart.length > 0) {
      calculateAndUpdateTaxes(state.cart);
    } else {
      // Reset taxes when cart is empty
      dispatch({
        type: 'UPDATE_TOTALS',
        payload: {
          rateTotal: 0,
          offerDiscount: 0,
          subtotal: 0,
          taxes: 0,
          total: 0,
          finalTotal: 0,
          taxBreakdown: null
        }
      });
    }
  }, [state.cart, state.searchState.checkin, state.searchState.checkout]);


const formatDisplayDate = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) {
    return "Select date";
  }
  
  // FIX: Manual formatting to avoid timezone issues
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  // Use UTC methods to get the correct date components
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate().toString().padStart(2, '0');
  
  return `${month} ${day}, ${year}`;
};

  const calculateNights = () => {
  const { checkin, checkout } = state.searchState;
  // Ensure dates are valid Date objects
  const checkinDate = checkin instanceof Date ? checkin : new Date(checkin);
  const checkoutDate = checkout instanceof Date ? checkout : new Date(checkout);
  
  if (!checkinDate || !checkoutDate || isNaN(checkinDate.getTime()) || isNaN(checkoutDate.getTime())) {
    return 0;
  }
  
  // FIX: Use UTC to avoid timezone issues and calculate difference in days correctly
  const utcCheckin = Date.UTC(checkinDate.getFullYear(), checkinDate.getMonth(), checkinDate.getDate());
  const utcCheckout = Date.UTC(checkoutDate.getFullYear(), checkoutDate.getMonth(), checkoutDate.getDate());
  
  const diffTime = Math.abs(utcCheckout - utcCheckin);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Return the difference (checkout - checkin) in days
  // If checkout is same day as checkin, return 1 (minimum 1 night)
  return Math.max(1, diffDays);
};


  const calculateTaxesForCart = async (cart, checkin, checkout, shopId) => {
  if (!cart.length) return { taxes: 0, taxBreakdown: null };
  
  try {
    // Calculate total room price for the entire stay
    const nights = Math.max(1, Math.ceil((new Date(checkout) - new Date(checkin)) / (1000 * 3600 * 24)));
    const totalRoomRate = cart.reduce((sum, item) => sum + (item.roomPrice * item.quantity * nights), 0);
    
    // Calculate total room price per night (sum of all selected rooms per night)
    const totalPerNightRate = cart.reduce((sum, item) => sum + (item.roomPrice * item.quantity), 0);

    // Call API for tax calculation
    const formatDate = (date) => {
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    };
    
    const payload = {
      type: "tariff",
      amount: totalPerNightRate,
      check_in_date: formatDate(checkin),
      check_out_date: formatDate(checkout),
      id_shop: SHOP_ID
    };
    
    const response = await fetch(`${API_BASE_URL}/fo/mstCalculation`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (response.ok && data.total_tax) {
      const totalTax = data.total_tax;
      const taxes = (totalTax.total_cgst_amount_after_tax || 0) + 
                    (totalTax.total_sgst_amount_after_tax || 0) + 
                    (totalTax.total_cess_amount || 0);
      
      console.log('taxes log',taxes);
      return {
        taxes,
        taxBreakdown: {
          cgst: totalTax.total_cgst_amount_after_tax || 0,
          sgst: totalTax.total_sgst_amount_after_tax || 0,
          cess: totalTax.total_cess_amount || 0,
          totalTaxPercentage: ((taxes / totalTax.base_amount_total) * 100).toFixed(2)
        }
      };
    }
    
    // Fallback to static tax rate if API fails
    console.warn('Tax API failed, using static 18%');
    return {
      taxes: totalRoomRate * TAX_RATE,
      taxBreakdown: null
    };
    
  } catch (error) {
    console.error('Tax calculation error:', error);
    // Fallback
    const nights = Math.max(1, Math.ceil((new Date(checkout) - new Date(checkin)) / (1000 * 3600 * 24)));
    const totalRoomRate = cart.reduce((sum, item) => sum + (item.roomPrice * item.quantity * nights), 0);
    return {
      taxes: totalRoomRate * TAX_RATE,
      taxBreakdown: null
    };
  }
};

  const updateTotals = (cart, nights) => {
    const rateTotal = cart.reduce((acc, item) => acc + (item.roomPrice * item.quantity * nights), 0);
    
    // Calculate offer discounts
    let offerDiscount = 0;
    cart.forEach(item => {
      if (item.offerFreeNights > 0 && nights >= 3) {
        offerDiscount += (item.roomPrice * item.offerFreeNights * item.quantity);
      }
    });
    
    const subtotal = rateTotal - offerDiscount;
    const taxes = subtotal * TAX_RATE;
    const total = subtotal + taxes;

    return {
      rateTotal,
      offerDiscount,
      subtotal,
      taxes,
      total,
      finalTotal: total
    };
  };

  return (
    <BookingContext.Provider value={{
      state,
      dispatch,
      formatCurrency,
      formatDisplayDate,
      calculateNights,
      updateTotals,
      calculateAndUpdateTaxes, // Export this function
      TAX_RATE,
      DISCOUNT_AMOUNT
    }}>
      {children}
    </BookingContext.Provider>
  );
}

export const useBooking = () => {
  const context = useContext(BookingContext);
  if (!context) {
    throw new Error('useBooking must be used within a BookingProvider');
  }
  return context;
};